import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  styled,
  Divider,
  MenuItem,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  HeroSection,
  HeroTitle,
  HeroSubtitle,
} from "../../../components/hero-section/HeroSection";
import useContact from "../../../hooks/api-request/useContact";

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: "#fff",
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ContactCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  "& .MuiSvgIcon-root": {
    color: theme.palette.primary.main,
    fontSize: 28,
  },
}));

const MapContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "300px",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  "& iframe": {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const { sendMessage, loading, SnackbarMessage } = useContact();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    subject: "",
    message: "",
    enquiry_type: "GENERAL",
  });

  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t('help.contactUs.errors.fullNameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('help.contactUs.errors.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('help.contactUs.errors.invalidEmail');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('help.contactUs.errors.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('help.contactUs.errors.messageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await sendMessage(formData);
        // Reset form on success
        setFormData({
          full_name: "",
          email: "",
          subject: "",
          message: "",
          enquiry_type: "GENERAL",
        });
      } catch (error) {
        console.error("Contact form submission failed:", error);
      }
    }
  };

  const enquiryTypes = [
    { value: "GENERAL", label: t('help.contactUs.enquiryTypes.general') },
    { value: "SUPPORT", label: t('help.contactUs.enquiryTypes.support') },
    { value: "SALES", label: t('help.contactUs.enquiryTypes.sales') },
    { value: "FEEDBACK", label: t('help.contactUs.enquiryTypes.feedback') },
    { value: "OTHER", label: t('help.contactUs.enquiryTypes.other') },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <HeroTitle>{t('help.contactUs.title')}</HeroTitle>
              <HeroSubtitle>
                {t('help.contactUs.subtitle')}
              </HeroSubtitle>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Contact Information Section */}
      <Box sx={{ py: 6, backgroundColor: "background.default" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Contact Info Cards */}
            <Grid item xs={12} md={4}>
              <ContentPaper elevation={0}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  {t('help.contactUs.contactInfo')}
                </Typography>

                <ContactCard>
                  <LocationIcon />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {t('help.contactUs.visitUs')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('help.contactUs.address.line1')}
                      <br />
                      {t('help.contactUs.address.line2')}
                      <br />
                      {t('help.contactUs.address.line3')}
                      <br />
                      {t('help.contactUs.address.line4')}
                    </Typography>
                  </Box>
                </ContactCard>

                <ContactCard>
                  <EmailIcon />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {t('help.contactUs.emailUs')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      info@hasabookstore.com
                    </Typography>
                  </Box>
                </ContactCard>

                <ContactCard>
                  <TimeIcon />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {t('help.contactUs.responseTime')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('help.contactUs.responseTimeText')}
                    </Typography>
                  </Box>
                </ContactCard>

                <Divider sx={{ my: 3 }} />

                <MapContainer>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3286.8876811925877!2d69.11840847677933!3d34.51217397297676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38d16f6aaaaaaaaa%3A0x0!2zMzTCsDMwJzQzLjgiTiA2OcKwMDcnMTUuNiJF!5e0!3m2!1sen!2s!4v1709125547955!5m2!1sen!2s"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t('help.contactUs.mapTitle')}
                  />
                </MapContainer>
              </ContentPaper>
            </Grid>

            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <ContentPaper elevation={0}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  {t('help.contactUs.sendMessage')}
                </Typography>

                <StyledForm onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('help.contactUs.form.fullName')}
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        error={!!errors.full_name}
                        helperText={errors.full_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('help.contactUs.form.email')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label={t('help.contactUs.form.enquiryType')}
                        name="enquiry_type"
                        value={formData.enquiry_type}
                        onChange={handleChange}
                      >
                        {enquiryTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('help.contactUs.form.subject')}
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        error={!!errors.subject}
                        helperText={errors.subject}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={t('help.contactUs.form.message')}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        error={!!errors.message}
                        helperText={errors.message}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      height: 48,
                      mt: 2,
                      background:
                        "linear-gradient(135deg, #FF4C00 0%, #FF8A00 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #FF8A00 0%, #FF4C00 100%)",
                      },
                    }}
                  >
                    {loading ? t('help.contactUs.sending') : t('help.contactUs.sendMessageButton')}
                  </Button>
                </StyledForm>
              </ContentPaper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {SnackbarMessage}
    </Box>
  );
};

export default ContactUs;
